import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useHasFinePointer, usePrefersReducedMotion } from '../hooks/useMedia';
type Props = {
  className?: string;
};
const SPRING = {
  stiffness: 120,
  damping: 18,
  mass: 0.6
};

/**
 * "Identity Object" — an art-directed geometric composition for the hero.
 * Layered monogram construction in the Morvix blue palette, with a very
 * subtle pointer parallax. Decorative only; hidden from assistive tech.
 */
export function HeroVisual({
  className
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const fine = useHasFinePointer();
  const reduced = usePrefersReducedMotion();
  const interactive = fine && !reduced;
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);
  const frameX = useTransform(sx, [-1, 1], [6, -6]);
  const frameY = useTransform(sy, [-1, 1], [6, -6]);
  const markX = useTransform(sx, [-1, 1], [-12, 12]);
  const markY = useTransform(sy, [-1, 1], [-12, 12]);
  const chipX = useTransform(sx, [-1, 1], [-20, 20]);
  const chipY = useTransform(sy, [-1, 1], [-14, 14]);
  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    px.set((event.clientX - rect.left) / rect.width * 2 - 1);
    py.set((event.clientY - rect.top) / rect.height * 2 - 1);
  };
  const reset = () => {
    px.set(0);
    py.set(0);
  };
  return <div ref={ref} onMouseMove={handleMove} onMouseLeave={reset} className={`relative aspect-square select-none ${className ?? ''}`} aria-hidden="true" data-id="element-291" data-mp="3k6caf55frsct">
      {/* Frame + metadata */}
      <motion.div className="absolute inset-0 border border-line" style={interactive ? {
      x: frameX,
      y: frameY
    } : undefined} data-id="element-292" data-mp="3b54cwpaquvjs">
        <span className="label absolute -top-6 left-0 text-muted" data-id="element-293" data-mp="1zsbnhl5fej6w">Identity Object</span>
        <span className="label absolute -top-6 right-0 text-brand" data-id="element-294" data-mp="5bae0he24v0s">01</span>
        <span className="label absolute -bottom-6 left-0 text-muted" data-id="element-295" data-mp="5dehiy81ns0j">Morvix / Blue 0511F2</span>
        <span className="absolute -left-1 -top-1 h-2 w-2 border-l border-t border-brand" data-id="element-296" data-mp="eti310m9cd5" />
        <span className="absolute -right-1 -top-1 h-2 w-2 border-r border-t border-brand" data-id="element-297" data-mp="3rp1wt39fqcgz" />
        <span className="absolute -bottom-1 -left-1 h-2 w-2 border-b border-l border-brand" data-id="element-298" data-mp="3npb24ogsfc76" />
        <span className="absolute -bottom-1 -right-1 h-2 w-2 border-b border-r border-brand" data-id="element-299" data-mp="3irrmbk4fyrd4" />
      </motion.div>

      {/* Rotating construction rings */}
      <motion.svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={interactive ? {
      x: frameX,
      y: frameY
    } : undefined} data-id="element-300" data-mp="38rr2lx2nu98e">
        <motion.g style={{
        originX: '50px',
        originY: '50px'
      }} animate={reduced ? undefined : {
        rotate: 360
      }} transition={{
        duration: 90,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-301" data-mp="170xdoj6s8fnq">
          <circle cx="50" cy="50" r="34" className="fill-none stroke-line" strokeWidth="0.4" data-id="element-302" data-mp="2kxgvyfu47xs8" />
          <circle cx="50" cy="50" r="42" className="fill-none stroke-lav/50" strokeWidth="0.4" strokeDasharray="1.5 4" data-id="element-303" data-mp="2pvs3gjwxtw7a" />
        </motion.g>
        <motion.g style={{
        originX: '50px',
        originY: '50px'
      }} animate={reduced ? undefined : {
        rotate: -360
      }} transition={{
        duration: 60,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-304" data-mp="2ynal6z9j8k2h">
          <circle cx="50" cy="50" r="26" className="fill-none stroke-brand/40" strokeWidth="0.5" strokeDasharray="20 8" data-id="element-305" data-mp="26xz5zx532eha" />
        </motion.g>
        {/* Crosshair guides */}
        <line x1="0" y1="50" x2="100" y2="50" className="stroke-line" strokeWidth="0.3" data-id="element-306" data-mp="3me6jmf8hudip" />
        <line x1="50" y1="0" x2="50" y2="100" className="stroke-line" strokeWidth="0.3" data-id="element-307" data-mp="3rd0zp9f2tnz4" />
      </motion.svg>

      {/* Layered monogram */}
      <motion.svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={interactive ? {
      x: markX,
      y: markY
    } : undefined} data-id="element-308" data-mp="3pwwz88vjqw2x">
        <motion.polyline points="24,72 24,30 50,58 76,30 76,72" className="fill-none stroke-lav/70" strokeWidth="5" strokeLinejoin="miter" transform="translate(4 5)" initial={reduced ? {
        pathLength: 1
      } : {
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1.6,
        delay: 0.7,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-309" data-mp="2rnuolt2jt9v" />
        <motion.polyline points="24,72 24,30 50,58 76,30 76,72" className="fill-none stroke-brand" strokeWidth="5.5" strokeLinejoin="miter" initial={reduced ? {
        pathLength: 1
      } : {
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1.6,
        delay: 0.55,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-310" data-mp="mfetc6zc3z55" />
      </motion.svg>

      {/* Floating layered elements */}
      <motion.div className="absolute right-[8%] top-[12%] h-10 w-10 border border-brand/60 bg-brand/10 backdrop-blur-[1px]" style={interactive ? {
      x: chipX,
      y: chipY
    } : undefined} animate={reduced ? undefined : {
      rotate: [0, 12, 0]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-311" data-mp="1a31w2ow7aft2" />
      <motion.div className="absolute bottom-[14%] left-[6%] h-14 w-6 bg-brand" style={interactive ? {
      x: chipY,
      y: chipX
    } : undefined} animate={reduced ? undefined : {
      scaleY: [1, 0.82, 1]
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-312" data-mp="rcare0n5rdl0" />
      <motion.div className="absolute bottom-[22%] right-[14%] h-2 w-2 rounded-full bg-lav" style={interactive ? {
      x: chipX,
      y: chipY
    } : undefined} data-id="element-313" data-mp="mcoe5r7bxxc7" />
    </div>;
}